import {
  useCallback,
  useRef
} from "react";
import { Text } from "@react-three/drei";
import * as THREE from "three";
import {
  useFrame,
  useThree,
} from "@react-three/fiber";
import uuid from "react-uuid";
import { rtfoConversionFactors } from "./rtfoConversionFactors";
// import { toPositiveAngle } from "./geometryFunctions";

export function handleRotation(ov, dv, rotation) {
  const vector = new THREE.Vector3().subVectors(dv, ov);

  // Create a quaternion from Euler angles
  const quaternion = new THREE.Quaternion();
  quaternion.setFromEuler(new THREE.Euler(
    rotation[0],
    rotation[1],
    rotation[2],
    'XYZ'
  ));

  // Apply the quaternion to the vector
  vector.applyQuaternion(quaternion);

  const newPoint = new THREE.Vector3().addVectors(ov, vector);
  return newPoint;
};

function getGridAxisLocation(
  position,
  size,
  divisions,
  divisionIndex
) {
  const t = divisionIndex / (divisions);
  if (position >= 0) {
    const result = (position - size / 2) + (t * size);
    return result;
  }
  else {
    const result = (position + size / 2) - (t * size);
    return result;
  };
};

function handleAutoRotation(
  rotation
) {
  return rotation;
};

/**
  * @param Props {Object}
  * @param Props.size {number}
  * @param Props.rotation {[number, number, number]}
  * @param Props.markPadding {number}
  * @param Props.position {[number, number, number]}
  * @param Props.scale {[number, number, number]}
  * @param Props.divisions {number}
  * @param Props.markTopAxis {number} 
  * @param Props.markLeftAxis {number} 
  * @param Props.markRightAxis {number} 
  * @param Props.markBottomAxis {number} 
  * @param Props.markTopRotation {[number, number, number]}
  * @param Props.markBottomRotation {[number, number, number]}
  * @param Props.markLeftRotation {[number, number, number]}
  * @param Props.markRightRotation {[number, number, number]}
  * @param Props.labelTopProps {Object}
  * @param Props.labelBottomProps {Object}
  * @param Props.labelRightProps {Object}
  * @param Props.labelLeftProps {Object}
  * */
export default function RTFOWellbore3DGridElement({
  size,
  rotation,
  markPadding,
  position,
  scale,
  divisions,
  markTopAxis,
  markLeftAxis,
  markRightAxis,
  markBottomAxis,
  markTopRotation,
  markLeftRotation,
  markRightRotation,
  markBottomRotation,
  markTopAxisAutoRotation,
  markBottomAxisAutoRotation,
  markLeftAxisAutoRotation,
  markRightAxisAutoRotation,
  labelTopProps,
  labelBottomProps,
  labelLeftProps,
  labelRightProps,
  units,
  darkMode
}) {
  const ref = useRef();
  const marksRef = useRef();
  const { camera } = useThree();

  useFrame(() => {
    if (!marksRef.current) return;
    for (const textRef of marksRef.current.children) {
      if (
        (
          (textRef.name === "markTop" || textRef.name === "labelTop") &&
          markTopAxisAutoRotation === true
        ) ||
        (
          (textRef.name === "markBottom" || textRef.name === "labelBottom") &&
          markBottomAxisAutoRotation === true
        ) ||
        (
          (textRef.name === "markLeft" || textRef.name === "labelLeft") &&
          markLeftAxisAutoRotation === true
        ) ||
        (
          (textRef.name === "markRight" || textRef.name === "labelRight") &&
          markRightAxisAutoRotation === true
        )
      ) {

        const textPosition = new THREE.Vector3().setFromMatrixPosition(textRef.matrixWorld);
        const cameraPosition = new THREE.Vector3().setFromMatrixPosition(camera.matrixWorld);
        const directionToCamera = new THREE.Vector3().subVectors(cameraPosition, textPosition).normalize();
        const currentRotation = new THREE.Euler().setFromQuaternion(textRef.quaternion);
        const textFront = new THREE.Vector3(0, 0, -1).applyEuler(currentRotation);
        const angle = directionToCamera.angleTo(textFront);

        if (angle < Math.PI / 2) {
          const currentQuaternion = textRef.quaternion.clone();
          const angleToAdd = Math.PI; // 45 graus
          const quaternionToAdd = new THREE.Quaternion();
          quaternionToAdd.setFromAxisAngle(new THREE.Vector3(0, 1, 0), angleToAdd);
          currentQuaternion.multiplyQuaternions(quaternionToAdd, currentQuaternion);
          textRef.quaternion.copy(currentQuaternion);
        };
      };
    };
  });

  const gridHelper = new THREE.GridHelper(size, size / divisions);
  gridHelper.position.set(...position);
  gridHelper.material = new THREE.LineBasicMaterial({
    color: darkMode ? 0xffffff : 0xaaaaaa,
    opacity: 0.4,
    transparent: true,
  });
  gridHelper.rotation.set(...rotation);
  gridHelper.scale.y = 0.5;

  const CartesianMarks = useCallback(() => {
    const marks = [];

    for (let i = 0; i <= size / divisions; i++) {
      if (markTopAxis !== undefined) {
        //getFirstAndFinalValues to define the axis that is going to be display as increasing or decreasing
        const firstNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            getGridAxisLocation(position[0], size, size / divisions, 0),
            position[1],
            position[2] + size / 2 + (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        ).round();
        const lastNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            getGridAxisLocation(position[0], size, size / divisions, size / divisions),
            position[1],
            position[2] + size / 2 + (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        ).round();
        let vectorAxis = null;
        if (lastNewPosition.getComponent(0) !== firstNewPosition.getComponent(0)) {
          vectorAxis = 0;
        }
        else if (lastNewPosition.getComponent(1) !== firstNewPosition.getComponent(1)) {
          vectorAxis = 1;
        }
        else if (lastNewPosition.getComponent(2) !== firstNewPosition.getComponent(2)) {
          vectorAxis = 2;
        }
        else {
          throw new Error("Something is wrong in the generation of GRID");
        };
        const result = getGridAxisLocation(position[0], size, size / divisions, i);
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            result,
            position[1],
            position[2] + size / 2 + (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        ).round();
        marks.push(
          <Text
            position={newPosition}
            rotation={handleAutoRotation(markTopRotation, newPosition, markTopRotation)}
            color={darkMode ? "white" : "#aaa"} // default
            anchorX="center" // default
            anchorY="middle" // default
            name="markTop"
            key={uuid()}
          >
            {(Math.trunc(newPosition.getComponent(markTopAxis) * scale.getComponent(vectorAxis)) * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(0)}
          </Text >
        );
      };
      if (markBottomAxis !== undefined) {
        const firstNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            getGridAxisLocation(position[0], size, size / divisions, 0),
            position[1],
            position[2] - size / 2 - (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        ).round();
        const lastNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            getGridAxisLocation(position[0], size, size / divisions, size / divisions),
            position[1],
            position[2] - size / 2 - (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        ).round();

        let vectorAxis = null;

        if (lastNewPosition.getComponent(0) !== firstNewPosition.getComponent(0)) {
          vectorAxis = 0;
        }
        else if (lastNewPosition.getComponent(1) !== firstNewPosition.getComponent(1)) {
          vectorAxis = 1;
        }
        else if (lastNewPosition.getComponent(2) !== firstNewPosition.getComponent(2)) {
          vectorAxis = 2;
        }
        else {
          throw new Error("Something is wrong in the generation of GRID");
        }

        const result = getGridAxisLocation(position[0], size, size / divisions, i);
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            result,
            position[1],
            position[2] - size / 2 - (typeof markPadding === "number" ? markPadding : 0)
          ),
          rotation
        );
        marks.push(
          <Text
            position={newPosition}
            rotation={markBottomRotation}
            color={darkMode ? "white" : "#aaa"} // default
            anchorX="center" // default
            anchorY="middle" // default
            name="markBottom"
            key={uuid()}
          >
            {(Math.trunc(newPosition.getComponent(markBottomAxis) * scale.getComponent(vectorAxis)) * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(0)}
          </Text >
        );
      };
      if (markLeftAxis !== undefined) {
        const firstNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] - size / 2 - (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            getGridAxisLocation(position[2], size, size / divisions, 0),
          ),
          rotation
        ).round();
        const lastNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] - size / 2 - (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            getGridAxisLocation(position[2], size, size / divisions, size / divisions),
          ),
          rotation
        ).round();

        let vectorAxis = null;

        if (lastNewPosition.getComponent(0) !== firstNewPosition.getComponent(0)) {
          vectorAxis = 0;
        }
        else if (lastNewPosition.getComponent(1) !== firstNewPosition.getComponent(1)) {
          vectorAxis = 1;
        }
        else if (lastNewPosition.getComponent(2) !== firstNewPosition.getComponent(2)) {
          vectorAxis = 2;
        }
        else {
          throw new Error("Something is wrong in the generation of GRID");
        };
        const result = getGridAxisLocation(position[2], size, size / divisions, i);
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] - size / 2 - (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            result
          ),
          rotation
        );
        marks.push(
          <Text
            position={newPosition}
            rotation={markLeftRotation}
            color={darkMode ? "white" : "#aaa"} // default
            anchorX="center" // default
            anchorY="middle" // default
            name="markLeft"
            key={uuid()}
          >
            {(Math.trunc(newPosition.getComponent(markLeftAxis) * scale.getComponent(vectorAxis)) * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(0)}
          </Text >
        );
      };
      if (markRightAxis !== undefined) {
        const firstNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] + size / 2 + (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            getGridAxisLocation(position[2], size, size / divisions, 0),
          ),
          rotation
        ).round();
        const lastNewPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] + size / 2 + (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            getGridAxisLocation(position[2], size, size / divisions, size / divisions),
          ),
          rotation
        ).round();

        let vectorAxis = null;

        if (lastNewPosition.getComponent(0) !== firstNewPosition.getComponent(0)) {
          vectorAxis = 0;
        }
        else if (lastNewPosition.getComponent(1) !== firstNewPosition.getComponent(1)) {
          vectorAxis = 1;
        }
        else if (lastNewPosition.getComponent(2) !== firstNewPosition.getComponent(2)) {
          vectorAxis = 2;
        }
        else {
          throw new Error("Something is wrong in the generation of GRID");
        };
        const result = getGridAxisLocation(position[2], size, size / divisions, i);
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] + size / 2 + (typeof markPadding === "number" ? markPadding : 0),
            position[1],
            result
          ),
          rotation
        );
        marks.push(
          <Text
            position={newPosition}
            rotation={markRightRotation}
            color={darkMode ? "white" : "#aaa"} // default
            anchorX="center" // default
            anchorY="middle" // default
            name="markRight"
            key={uuid()}
          >
            {(Math.trunc(newPosition.getComponent(markRightAxis) * scale.getComponent(vectorAxis)) * (units === 'field' ? rtfoConversionFactors.length : 1))?.toFixed(0)}
          </Text >
        );
      };
    };
    return marks;
  }, [size, divisions, markTopAxis, markBottomAxis, markLeftAxis, markRightAxis, position, markPadding, rotation, markTopRotation, darkMode, scale, units, markBottomRotation, markLeftRotation, markRightRotation]);

  const Labels = useCallback(() => {
    const labels = [];
    if (
      size !== undefined &&
      divisions !== undefined &&
      rotation !== undefined
    ) {
      if (labelTopProps !== undefined) {
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0],
            position[1],
            position[2] + size / 2 + (typeof markPadding === "number" ? markPadding + size * 0.03 : 0)
          ),
          rotation
        );

        labels.push(
          <Text
            {...labelTopProps}
            position={newPosition}
            name="labelTop"
            key={uuid()}
            color={darkMode ? "white" : "#aaa"}
          />
        );
      };
      if (labelBottomProps !== undefined) {
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0],
            position[1],
            position[2] - size / 2 - (typeof markPadding === "number" ? markPadding + size * 0.03 : 0)
          ),
          rotation
        );

        labels.push(
          <Text
            {...labelBottomProps}
            position={newPosition}
            name="labelBottom"
            key={uuid()}
            color={darkMode ? "white" : "#aaa"}
          />
        );
      };
      if (labelLeftProps !== undefined) {
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] - size / 2 - (typeof markPadding === "number" ? markPadding + size * 0.03 : 0),
            position[1],
            position[2]
          ),
          rotation
        );

        labels.push(
          <Text
            {...labelLeftProps}
            position={newPosition}
            name="labelLeft"
            key={uuid()}
            color={darkMode ? "white" : "#aaa"}
          />
        );
      };
      if (labelRightProps !== undefined) {
        const newPosition = handleRotation(
          new THREE.Vector3(...position),
          new THREE.Vector3(
            position[0] + size / 2 + (typeof markPadding === "number" ? markPadding + size * 0.03 : 0),
            position[1],
            position[2]
          ),
          rotation
        );
        labels.push(
          <Text
            {...labelRightProps}
            position={newPosition}
            name="labelRight"
            key={uuid()}
            color={darkMode ? "white" : "#aaa"}
          />);
      };
    };
    return labels;
  }, [size, divisions, rotation, labelTopProps, labelBottomProps, labelLeftProps, labelRightProps, position, markPadding, darkMode]);

  return (
    <group key={uuid()} ref={marksRef}>
      <Labels />
      <CartesianMarks />
      <primitive ref={ref} object={gridHelper} />
    </group>
  );
};
